import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { Skeleton } from '@mui/material';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import css from './CreativeView.css';
import { CreativeType } from '../../types';
import { TRACK_EVENT_PREFIX } from '../../consts';
import CreativeAsset from '../creativeAsset/CreativeAsset';
import { formatValue, getCreativeDisplayName } from '../../creativesFormattingUtils';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { trackMixpanelEvent } from '../../../utils/general';
import Tooltip from '../../../components/molecules/Tooltip';
import CreativeDimensionTags from './creativeDimensionTags/CreativeDimensionTags';
import { getAdminModeEnabled } from '../../../selectors/user';

const ASSET_ASPECT_RATIO = 550 / 400;
const SIMILAR_CREATIVE_ASPECT_RATIO = 90 / 70;
const SIMILAR_CREATIVES_SKELETONS = [...Array(4)];
const SIMILAR_CREATIVES_KEY_PREFIX = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.SIMILAR_CREATIVES';

export const getDimensionsTranslationKey = key => `STATIC.PAGES.CREATIVE_GALLERY.DIMENSIONS_INFO.${key}`;

const CreativeView = ({
    ref,
    creative,
    app,
    source,
    similarCreatives = [],
    isLoadingSimilarCreatives = false,
    similarCreativesError = false,
    onSimilarCreativeClick: onSimilarCreativeClickProp,
}) => {
    const [videoDuration, setVideoDuration] = useState(null);
    const isAdminMode = useSelector(getAdminModeEnabled);

    const { assetIdList, assetNameList, creativeIdList, creativeNameList } = creative.multiValuedDimensions;
    const isAssetName = !!assetNameList?.length;
    const isAssetId = !!assetIdList?.length;

    const handleVideoLoad = () => {
        if (ref?.current?.duration) setVideoDuration(ref.current.duration);
    };

    const onSimilarCreativeClick = (e, similarCreativeHash) => {
        e.preventDefault();
        trackMixpanelEvent(TRACK_EVENT_PREFIX, 'Similar Creative Clicked', {
            app: app.label,
            source: source.label,
            creativeType: creative.type,
        });
        onSimilarCreativeClickProp(similarCreativeHash);
    };

    return (
        <div className={css.creativeView}>
            <CreativeDimensionTags creative={creative} source={source} app={app} videoDuration={videoDuration} />
            <CreativeAsset
                key={creative.imageHash}
                ref={ref}
                type={creative.type}
                url={creative.url}
                embeddingUrl={creative.embeddingUrl}
                className={css.creativeAsset}
                controls
                aspectRatio={ASSET_ASPECT_RATIO}
                onLoad={handleVideoLoad}
                muted={false}
            />
            <div className={css.creativeRawData}>
                <Translate
                    id={getDimensionsTranslationKey(`${isAssetName ? 'ASSET' : 'CREATIVE'}_NAME`)}
                    data={{ value: formatValue(isAssetName ? assetNameList : creativeNameList || []) }}
                />
                <Translate
                    id={getDimensionsTranslationKey(`${isAssetId ? 'ASSET' : 'CREATIVE'}_ID`)}
                    data={{ value: formatValue(isAssetId ? assetIdList : creativeIdList || []) }}
                />
            </div>
            <div className={css.similarCreativesSection}>
                {!similarCreativesError && (
                    <Translate
                        id={`${SIMILAR_CREATIVES_KEY_PREFIX}.${isLoadingSimilarCreatives ? 'LOADING' : 'TITLE'}`}
                        data={{ count: similarCreatives.length }}
                    />
                )}
                <div className={css.container}>
                    {isLoadingSimilarCreatives ? (
                        SIMILAR_CREATIVES_SKELETONS.map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rectangular"
                                className={css.similarCreativeItem}
                                animation="wave"
                            />
                        ))
                    ) : similarCreativesError ? (
                        <Translate id={`${SIMILAR_CREATIVES_KEY_PREFIX}.ERROR`} />
                    ) : (
                        similarCreatives.map(({ type, url, embeddingUrl, imageHash, multiValuedDimensions, similarityScore }) => {
                            const tooltipTitle = getCreativeDisplayName(multiValuedDimensions);
                            return (
                                <div key={url} className={css.similarCreativeWrapper}>
                                    <Tooltip title={tooltipTitle}>
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={e => onSimilarCreativeClick(e, imageHash)}
                                            className={css.clickableCreative}
                                        >
                                            <CreativeAsset
                                                type={type}
                                                showPlayable={false}
                                                url={url}
                                                aspectRatio={SIMILAR_CREATIVE_ASPECT_RATIO}
                                                embeddingUrl={embeddingUrl}
                                                className={classNames(css.similarCreativeItem, css.asset)}
                                            />
                                        </a>
                                    </Tooltip>
                                    {isAdminMode && typeof similarityScore === 'number' && (
                                        <div className={css.similarityScore}>
                                            {`Similarity: ${similarityScore.toFixed(3)}`}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

CreativeView.propTypes = {
    ref: PropTypes.shape({ current: Element }),
    creative: PropTypes.shape(CreativeType).isRequired,
    app: AutoCompleteOptionType.isRequired,
    source: AutoCompleteOptionType.isRequired,
    similarCreatives: PropTypes.arrayOf(PropTypes.shape(CreativeType)),
    isLoadingSimilarCreatives: PropTypes.bool,
    similarCreativesError: PropTypes.bool,
    onSimilarCreativeClick: PropTypes.func.isRequired,
};

export default CreativeView;
